import { useEffect, useState } from 'react';
import { DEFAULT_NOTIFY_PREFS, type NotifyEvent, type NotifyPrefs } from '@machs/shared';
import { api } from '../lib/api';
import { Icon } from '../lib/icons';
import {
  canPromptInstall,
  disablePush,
  enablePush,
  isSafari,
  isStandalone,
  onInstallChange,
  promptInstall,
  pushActive,
  pushSupported,
} from '../lib/push';
import { Dialog, useToast } from './ui';

const EVENTS: { key: NotifyEvent; label: string; hint: string }[] = [
  { key: 'nachricht', label: 'Neue Nachrichten', hint: 'Text, Fotos, Videos, Sprachnachrichten und Dateien' },
  { key: 'aufgabe', label: 'Neue Aufgaben', hint: 'Jemand legt eine Aufgabe an' },
  { key: 'zuweisung', label: 'Zuweisungen', hint: 'Dir wird eine Aufgabe zugewiesen' },
  { key: 'rueckfrage', label: 'Rückfragen', hint: 'Eine Rückfrage an dich oder an alle' },
  { key: 'erwaehnung', label: 'Erwähnungen', hint: 'Jemand nennt dich mit @ in einer Nachricht' },
  { key: 'antwort', label: 'Antworten an dich', hint: 'Jemand antwortet auf deine Nachricht oder Rückfrage' },
  { key: 'reaktion', label: 'Reaktionen', hint: 'Jemand reagiert auf deine Nachricht' },
];

/**
 * Benachrichtigungen: auf diesem Gerät ein- und ausschalten, dazu die Anlässe
 * (gelten für alle Geräte) und die Installation als App.
 */
export function NotifyDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [active, setActive] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState<NotifyPrefs | null>(null);
  const [, rerender] = useState(0);
  const denied = pushSupported() && Notification.permission === 'denied';

  useEffect(() => {
    void pushActive().then(setActive, () => setActive(false));
    void api<NotifyPrefs>('/push/prefs').then(setPrefs, () => setPrefs(DEFAULT_NOTIFY_PREFS));
    return onInstallChange(() => rerender((n) => n + 1));
  }, []);

  async function toggle() {
    setBusy(true);
    try {
      if (active) {
        await disablePush();
        setActive(false);
      } else {
        await enablePush();
        setActive(true);
        toast('Benachrichtigungen sind eingeschaltet');
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Hat nicht geklappt');
    } finally {
      setBusy(false);
    }
  }

  async function save(next: NotifyPrefs) {
    setPrefs(next);
    try {
      await api('/push/prefs', { method: 'PUT', json: next });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  }

  async function probe() {
    const r = await api<{ sent: number }>('/push/test', { method: 'POST' }).catch(() => ({ sent: 0 }));
    if (!r.sent) toast('Keine Probe verschickt, kein Gerät angemeldet');
  }

  return (
    <Dialog title="Benachrichtigungen" onClose={onClose}>
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <span className="notifyicon" data-on={active ? 'true' : 'false'}><Icon name="bell" size={18} /></span>
            <span className="min-w-0 flex-1">
              <b className="block text-[14.5px]">Auf diesem Gerät</b>
              <small className="block text-[12.5px] text-[var(--muted)]">
                {!pushSupported()
                  ? 'Dieser Browser kann keine Benachrichtigungen empfangen.'
                  : denied
                    ? 'Im Browser blockiert. Erlaube Benachrichtigungen in den Website-Einstellungen (Schloss-Symbol links neben der Adresse).'
                    : active
                      ? 'Eingeschaltet. Neues meldet sich auch, wenn Mach’s geschlossen ist.'
                      : 'Ausgeschaltet.'}
              </small>
            </span>
            {pushSupported() && !denied && (
              <button className={`switch ${active ? 'on' : ''}`} role="switch" aria-checked={!!active} aria-label="Benachrichtigungen auf diesem Gerät" disabled={busy || active === null} onClick={() => void toggle()} />
            )}
          </div>
          {active && (
            <button className="btn btn-ghost self-start" onClick={() => void probe()}><Icon name="send" size={16} />Probe schicken</button>
          )}
        </section>

        {prefs && (
          <section className="flex flex-col gap-3 border-t border-[var(--line)] pt-4">
            <div>
              <h3 className="m-0 text-[14px] font-semibold">Worüber</h3>
              <p className="m-0 mt-0.5 text-[12.5px] text-[var(--muted)]">Gilt für alle deine Geräte. Einzelne Aufgaben schaltest du in ihren Angaben stumm.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="chip" aria-pressed={prefs.scope === 'alle'} onClick={() => void save({ ...prefs, scope: 'alle' })}>Alle Aufgaben</button>
              <button className="chip" aria-pressed={prefs.scope === 'beteiligt'} onClick={() => void save({ ...prefs, scope: 'beteiligt' })}
                data-tip="Angelegt, zugewiesen, angepinnt oder mitgeschrieben" data-tip-wrap>
                Nur, woran ich beteiligt bin
              </button>
            </div>
            <ul className="notifylist">
              {EVENTS.map((e) => (
                <li key={e.key}>
                  <span className="min-w-0 flex-1">
                    <b className="block text-[14px] font-medium">{e.label}</b>
                    <small className="block text-[12.5px] text-[var(--muted)]">{e.hint}</small>
                  </span>
                  <button
                    className={`switch ${prefs.events[e.key] ? 'on' : ''}`}
                    role="switch"
                    aria-checked={prefs.events[e.key]}
                    aria-label={e.label}
                    onClick={() => void save({ ...prefs, events: { ...prefs.events, [e.key]: !prefs.events[e.key] } })}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="flex flex-col gap-2 border-t border-[var(--line)] pt-4">
          <h3 className="m-0 text-[14px] font-semibold">Als App installieren</h3>
          {isStandalone() ? (
            <p className="m-0 text-[13px] text-[var(--muted)]">Mach&rsquo;s läuft hier bereits als App. Neues zeigt die Zahl am Symbol im Dock.</p>
          ) : canPromptInstall() ? (
            <>
              <p className="m-0 text-[13px] text-[var(--muted)]">Eigenes Fenster, Symbol im Dock und die Zahl ungelesener Nachrichten am Symbol.</p>
              <button className="btn btn-primary self-start" onClick={() => void promptInstall()}><Icon name="screen" size={16} />Installieren</button>
            </>
          ) : isSafari() ? (
            <p className="m-0 text-[13px] text-[var(--muted)]">In Safari: Menü <b>Ablage</b> &rsaquo; <b>Zum Dock hinzufügen</b>. Danach Mach&rsquo;s aus dem Dock starten und hier die Benachrichtigungen einschalten.</p>
          ) : (
            <p className="m-0 text-[13px] text-[var(--muted)]">
              In Chrome oder Edge über das Symbol rechts in der Adressleiste oder im Menü unter <b>Streamen, speichern und teilen</b> &rsaquo; <b>Seite als App installieren</b>.
            </p>
          )}
        </section>
      </div>
    </Dialog>
  );
}
